import type { EventBus } from '../../core/events';
import type { RunState } from '../../game/RunState';
import { LAYOUT } from '../../world/road/roadLayout';
import { activeRoute, tripAt } from '../../world/route';
import type { DrivingAssist } from '../driving';
import { laneX } from '../lanes';

/**
 * The U-turn at the end of every trip. The trotro brakes, swings left across the gap in the median onto the
 * other carriageway and comes back the way it came. All distances in metres, speeds in m/s.
 */
export const UTURN = {
  /** The turn starts this far before the end of the road. */
  startBefore: 28,
  /** Braking for the turn begins this far before it starts, at `decel`. */
  brakeRange: 110,
  decel: 7,
  /** Speed through the turn. */
  speed: 8,
  /** How far the turn bulges forward. */
  depth: 6.5,
  /** The lane you come out in on the way back (0 = the one by the median). */
  endLane: 0,
  /** No traffic, gifts or checkpoints are set up in the last stretch before the turn. */
  quietZone: 170,
} as const;

const OFF: DrivingAssist = { maxSpeed: Number.POSITIVE_INFINITY };

/** Where the current trip ends (world distance). */
export function tripEndOf(s: number): number {
  return (tripAt(s).trip + 1) * activeRoute().length;
}

/** The last stretch of a trip, where the road empties for the turn. */
export function inQuietZone(s: number): boolean {
  return s >= tripEndOf(s) - UTURN.quietZone;
}

/** A one-way journey brings the trotro to rest this far before the end of the road. */
const ARRIVE_BEFORE_END = 22;

/** Brakes the trotro down to turning speed as it reaches the turn (or to a halt at the end of a one-way journey). */
export function uTurnAssist(state: RunState): DrivingAssist {
  if (activeRoute().oneWay) {
    const toStop = tripEndOf(state.bus.s) - ARRIVE_BEFORE_END - state.bus.s;
    return toStop > UTURN.brakeRange ? OFF : { maxSpeed: Math.sqrt(2 * UTURN.decel * Math.max(0, toStop)) + 0.4 };
  }
  if (state.turn.active) return { maxSpeed: UTURN.speed };
  const toTurn = tripEndOf(state.bus.s) - UTURN.startBefore - state.bus.s;
  if (toTurn > UTURN.brakeRange) return OFF;
  return { maxSpeed: Math.sqrt(UTURN.speed ** 2 + 2 * UTURN.decel * Math.max(0, toTurn)) };
}

/** Where the trotro is on the turn: x and s from the angle round the half-ellipse, and which way its nose points. */
export function turnPose(theta: number, s0: number, x0: number, xEnd: number): { x: number; s: number; heading: number } {
  const across = (x0 - xEnd) / 2;
  return {
    x: (x0 + xEnd) / 2 + across * Math.cos(theta),
    s: s0 + UTURN.depth * Math.sin(theta),
    heading: Math.atan2(across * Math.sin(theta), UTURN.depth * Math.cos(theta)),
  };
}

function begin(state: RunState, events: EventBus): void {
  const { bus, turn } = state;
  const end = tripEndOf(bus.s);
  Object.assign(turn, { active: true, theta: 0, s0: bus.s, x0: bus.x, xEnd: 2 * LAYOUT.medianX - laneX(UTURN.endLane), end, heading: 0 });
  // Nothing else is on the road for the turn: cars near the turn make way, the oncoming lane is already empty.
  state.traffic = state.traffic.filter((car) => car.s < bus.s - 30 || car.s > bus.s + 80);
  bus.speed = Math.min(bus.speed, UTURN.speed);
  bus.invulnerable = Math.max(bus.invulnerable, 3);
  events.emit('uturn:started', {});
}

/** Swings round the bend, then lands on the other carriageway: the world turns 180° about the gap in the median. */
function finish(state: RunState, events: EventBus): void {
  const { bus, turn } = state;
  bus.s = 2 * turn.end - turn.s0;
  bus.x = laneX(UTURN.endLane);
  bus.yaw = 0;
  bus.lane = UTURN.endLane;
  bus.targetLane = UTURN.endLane;
  bus.vx = 0;
  bus.speed = UTURN.speed;
  bus.invulnerable = Math.max(bus.invulnerable, 1.5);
  turn.active = false;
  turn.heading = 0;
  turn.swaps += 1;
  state.oncoming = [];
  events.emit('uturn:done', {});
}

/** One-way journeys end here: once the trotro has rolled to a halt at the end of the road, the shift is over. */
function arrive(state: RunState, events: EventBus): void {
  const { bus } = state;
  if (state.arrived || bus.s < tripEndOf(bus.s) - ARRIVE_BEFORE_END - 4 || bus.speed > 1.2) return;
  state.arrived = true;
  state.over = true;
  state.endReason = 'endShift';
  events.emit('journey:arrived', {});
}

/** Starts the turn at the end of the road and drives the trotro round it. */
export function updateUTurn(state: RunState, dt: number, events: EventBus): void {
  const { bus, turn } = state;
  if (activeRoute().oneWay) return arrive(state, events);
  if (!turn.active) {
    if (bus.s < tripEndOf(bus.s) - UTURN.startBefore) return;
    begin(state, events);
  }
  const across = (turn.x0 - turn.xEnd) / 2;
  const pace = Math.hypot(across * Math.sin(turn.theta), UTURN.depth * Math.cos(turn.theta));
  turn.theta += (UTURN.speed / pace) * dt;
  if (turn.theta >= Math.PI) return finish(state, events);
  const pose = turnPose(turn.theta, turn.s0, turn.x0, turn.xEnd);
  bus.x = pose.x;
  bus.s = pose.s;
  bus.yaw = -pose.heading;
  bus.vx = 0;
  bus.y = 0;
  bus.vy = 0;
  bus.speed = UTURN.speed;
  turn.heading = pose.heading;
}
